/**
 * Cache management for Supernal TTS Widget
 */

import { CachedAudio, TTSResponse } from '../types';

export class CacheManager {
  private cache: Map<string, CachedAudio>;
  private cacheExpiry: number;
  private apiUrl: string;

  constructor(cacheExpiry: number, apiUrl: string) {
    this.cache = new Map();
    this.cacheExpiry = cacheExpiry;
    this.apiUrl = apiUrl;
    this.loadFromStorage();
  }

  /**
   * Generate hash for caching
   */
  public generateHash(text: string, options: any = {}): string {
    const str = text + JSON.stringify(options);
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Get cached audio URL by hash
   */
  public getCachedAudioUrl(hash: string): string | null {
    const cached = this.cache.get(hash);

    if (!cached) return null;

    if (Date.now() > cached.expiry) {
      this.cache.delete(hash);
      this.saveToStorage();
      return null;
    }

    const audioUrl = cached.audioUrl;
    return audioUrl.startsWith('http')
      ? audioUrl
      : `${this.apiUrl}${audioUrl}`;
  }

  /**
   * Cache audio URL with metadata
   */
  public cacheAudioUrl(hash: string, audioUrl: string, metadata: Partial<TTSResponse> = {}): void {
    this.cache.set(hash, {
      audioUrl,
      metadata,
      expiry: Date.now() + this.cacheExpiry,
      timestamp: Date.now()
    });

    this.saveToStorage();
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('supernal-tts-cache');
      if (stored) {
        const data = JSON.parse(stored);
        this.cache = new Map(Object.entries(data));
      }
    } catch (error) {
      console.warn('Failed to load TTS cache:', error);
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    try {
      const data = Object.fromEntries(this.cache);
      localStorage.setItem('supernal-tts-cache', JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save TTS cache:', error);
    }
  }

  /**
   * Clear all cache
   */
  public clear(): void {
    this.cache.clear();
    localStorage.removeItem('supernal-tts-cache');
  }

  /**
   * Get cache size
   */
  public get size(): number {
    return this.cache.size;
  }
}
